/**
 * RA Face Shape Quiz
 *
 * Drives the screen flow (intro → 3 questions → result) and computes the face
 * shape. Markup and copy live in sections/ra-face-quiz.liquid; this file only
 * reads data-* hooks, so nothing here needs to change when copy does.
 *
 * Scoring — each answer adds points to the shapes it suggests; the highest total
 * wins. Weights encode the results cheat sheet:
 *   Round     : similar length/width + rounded jaw
 *   Oval      : slightly longer + rounded jaw
 *   Square    : similar length/width + angular jaw
 *   Rectangle : noticeably longer + angular/straight jaw
 *   Heart     : forehead widest + narrow/pointed jaw
 *   Diamond   : cheekbones widest + narrow/pointed jaw
 * The jaw carries the most weight (it's the strongest signal), then the widest
 * area and the length ratio. Ties fall back to TIE_BREAK order.
 */
(function () {
  'use strict';

  var SCORES = {
    widest: {
      forehead: { heart: 2, oval: 1 },
      cheekbones: { diamond: 2, oval: 1 },
      similar: { round: 1, square: 1, rectangle: 1 }
    },
    jaw: {
      round: { round: 3, oval: 3 },
      angular: { square: 3, rectangle: 3 },
      pointed: { heart: 3, diamond: 3 }
    },
    ratio: {
      same: { round: 2, square: 2 },
      slightly: { oval: 2, heart: 1, diamond: 1 },
      longer: { rectangle: 2, oval: 1, diamond: 1 }
    }
  };

  var TIE_BREAK = ['heart', 'diamond', 'oval', 'round', 'square', 'rectangle'];

  function computeShape(answers) {
    var totals = {};
    Object.keys(SCORES).forEach(function (question) {
      var weights = SCORES[question][answers[question]] || {};
      Object.keys(weights).forEach(function (shape) {
        totals[shape] = (totals[shape] || 0) + weights[shape];
      });
    });

    return TIE_BREAK.reduce(function (best, shape) {
      var score = totals[shape] || 0;
      return score > best.score ? { shape: shape, score: score } : best;
    }, { shape: TIE_BREAK[0], score: -1 }).shape;
  }

  function initQuiz(root) {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    var screens = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-screen]'));
    var questions = screens.filter(function (screen) {
      return screen.dataset.quizScreen === 'question';
    });
    var resultScreen = root.querySelector('[data-quiz-screen="result"]');
    var answers = {};

    function showScreen(target) {
      screens.forEach(function (screen) {
        var active = screen === target;
        screen.hidden = !active;
        screen.classList.toggle('is-active', active);
      });
      window.scrollTo({ top: 0, behavior: 'auto' });
      // Screens swap without scrolling; nudge the header theme manager so the nav
      // flips between its light (hero) and dark (question/result) treatments.
      window.dispatchEvent(new Event('scroll'));
      // First visible heading (the result screen keeps one hidden panel per shape).
      var heading = Array.prototype.filter.call(target.querySelectorAll('h1, h2'), function (el) {
        return el.offsetParent !== null;
      })[0];
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    }

    function showResult() {
      var shape = computeShape(answers);
      resultScreen.querySelectorAll('[data-quiz-result]').forEach(function (panel) {
        panel.hidden = panel.dataset.quizResult !== shape;
      });
      root.dataset.quizShape = shape;
      showScreen(resultScreen);
    }

    root.querySelector('[data-quiz-start]').addEventListener('click', function () {
      showScreen(questions[0]);
    });

    questions.forEach(function (screen, index) {
      var group = screen.querySelector('[data-quiz-answer]');
      var key = group.dataset.quizAnswer;
      var options = Array.prototype.slice.call(group.querySelectorAll('[data-quiz-option]'));
      var next = screen.querySelector('[data-quiz-next]');
      var prev = screen.querySelector('[data-quiz-prev]');

      options.forEach(function (option) {
        option.addEventListener('click', function () {
          options.forEach(function (other) {
            var selected = other === option;
            other.classList.toggle('is-selected', selected);
            other.setAttribute('aria-checked', selected ? 'true' : 'false');
          });
          answers[key] = option.dataset.value;
          next.disabled = false;
        });
      });

      prev.addEventListener('click', function () {
        showScreen(index === 0 ? screens[0] : questions[index - 1]);
      });

      next.addEventListener('click', function () {
        if (!answers[key]) return;
        if (index === questions.length - 1) {
          showResult();
        } else {
          showScreen(questions[index + 1]);
        }
      });
    });
  }

  function boot() {
    document.querySelectorAll('[data-face-quiz]').forEach(initQuiz);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || typeof event.target.querySelector !== 'function') return;
    var root = event.target.querySelector('[data-face-quiz]');
    if (root) {
      root.dataset.ready = '';
      initQuiz(root);
      // Auglio scans for try-on buttons once, at page load; the theme editor re-renders the
      // section after that, so the new pills need a rescan to stay clickable.
      if (typeof window.virtooalRefreshBtns === 'function') window.virtooalRefreshBtns();
    }
  });
})();
